import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import SnackbarWrapper from "components/Snackbar";
import ConfirmationDialog from "components/ConfirmationDialog";
import LoadingScreen from "components/Loading";
import { useAuth } from "context/authContext";
import {
  getEmployeesBasicInfo,
  getPayrollRunEmployees,
  getPayrollResultsByRun,
  calculatePayrollRun,
  approvePayrollRun,
  rejectPayrollRun,
  postPayrollRun,
  payPayrollRun,
  populateRunEmployees,
} from "services/api";

const runStatusColors = {
  draft: "default",
  calculated: "info",
  approved: "secondary",
  posted: "warning",
  paid: "success",
};

const RunDetailView = ({ selectedPeriod, selectedRun, onRunUpdated, canEdit }) => {
  const { t } = useTranslation();
  const { employee } = useAuth();
  const snackbarRef = useRef();

  const [run, setRun] = useState(selectedRun);
  const [employees, setEmployees] = useState([]);
  const [runEmployees, setRunEmployees] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const [pendingAction, setPendingAction] = useState(null); // "approve" | "post" | "pay" | "calculate"

  useEffect(() => {
    setRun(selectedRun);
  }, [selectedRun]);

  useEffect(() => {
    loadData();
  }, [run?.id]);

  const loadData = async () => {
    if (!run) return;
    setLoading(true);
    const [empRes, runEmpRes, resultsRes] = await Promise.all([
      getEmployeesBasicInfo(),
      getPayrollRunEmployees(run.id),
      getPayrollResultsByRun(run.id),
    ]);
    if (empRes !== "error") setEmployees(empRes);
    setRunEmployees(runEmpRes !== "error" && Array.isArray(runEmpRes) ? runEmpRes : []);
    setResults(resultsRes !== "error" && Array.isArray(resultsRes) ? resultsRes : []);
    setLoading(false);
  };

  const employeeName = (id) => employees.find((e) => e.empId === id)?.empName || `#${id}`;

  const updateRunLocally = (updated) => {
    setRun(updated);
    onRunUpdated?.(updated);
  };

  const handlePopulate = async () => {
    const res = await populateRunEmployees(run.id);
    if (res == "error") {
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.runDetail.populateError"),
        "error"
      );
    }
  };

  const handleCalculate = async () => {
    if (runEmployees.length === 0) {
      await handlePopulate(); // Ensure employees are populated before calculating
    }
    const res = await calculatePayrollRun(run.id);
    if (res !== "error") {
      updateRunLocally(res);
      loadData(); // Refresh results + runEmployees so the table shows what was just calculated
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.runDetail.calculateSuccess"),
        "success"
      );
    } else {
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.runDetail.calculateError"),
        "error"
      );
    }
    setPendingAction(null);
  };

  const handleApprove = async () => {
    const res = await approvePayrollRun(run.id, employee?.id);
    if (res !== "error") {
      updateRunLocally(res);
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.runDetail.approveSuccess"),
        "success"
      );
    } else {
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.runDetail.approveError"),
        "error"
      );
    }
    setPendingAction(null);
  };

  const handlePost = async () => {
    const res = await postPayrollRun(run.id);
    if (res !== "error") {
      updateRunLocally(res);
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.runDetail.postSuccess"),
        "success"
      );
    } else {
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.runDetail.postError"),
        "error"
      );
    }
    setPendingAction(null);
  };

  const handlePay = async () => {
    const res = await payPayrollRun(run.id);
    if (res !== "error") {
      updateRunLocally(res);
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.runDetail.paySuccess"),
        "success"
      );
    } else {
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.runDetail.payError"),
        "error"
      );
    }
    setPendingAction(null);
  };

  const handleReject = async () => {
    const res = await rejectPayrollRun(run.id, rejectReason);
    if (res !== "error") {
      updateRunLocally(res);
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.runDetail.rejectSuccess"),
        "success"
      );
      setRejectOpen(false);
      setRejectReason("");
    } else {
      snackbarRef.current?.showSnackbar(
        t("admin.payroll.periodsAndRuns.runDetail.rejectError"),
        "error"
      );
    }
  };

  const confirmHandlers = {
    calculate: handleCalculate,
    approve: handleApprove,
    post: handlePost,
    pay: handlePay,
  };

  const confirmMessages = {
    calculate: t("admin.payroll.periodsAndRuns.runDetail.confirmCalculate"),
    approve: t("admin.payroll.periodsAndRuns.runDetail.confirmApprove"),
    post: t("admin.payroll.periodsAndRuns.runDetail.confirmPost"),
    pay: t("admin.payroll.periodsAndRuns.runDetail.confirmPay"),
  };

  const errorEmployees = runEmployees.filter((re) => re.status === "error");

  if (!run) {
    return (
      <Typography color="text.secondary">
        {t("admin.payroll.periodsAndRuns.noRunSelected")}
      </Typography>
    );
  }

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography variant="h6">
                {selectedPeriod?.name} —{" "}
                {t(`admin.payroll.periodsAndRuns.dashboard.runTypes.${run.runType}`)}
              </Typography>
              <Chip
                size="small"
                label={t(`admin.payroll.periodsAndRuns.dashboard.runStatuses.${run.status}`)}
                color={runStatusColors[run.status] || "default"}
              />
            </Box>
            {run.rejectionReason && (
              <MDTypography variant="caption" color="error" sx={{ display: "block" }}>
                {t("admin.payroll.periodsAndRuns.runDetail.lastRejection")}: {run.rejectionReason}
              </MDTypography>
            )}
            {!loading && (
              <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                {t("admin.payroll.periodsAndRuns.runDetail.includedCount", {
                  count: runEmployees.length,
                })}
              </MDTypography>
            )}
          </Box>

          {canEdit && (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {run.status === "draft" && (
                <MDButton size="small" color="info" onClick={() => setPendingAction("calculate")}>
                  {t("admin.payroll.periodsAndRuns.dashboard.calculate")}
                </MDButton>
              )}
              {run.status === "calculated" && (
                <>
                  <MDButton
                    size="small"
                    color="success"
                    onClick={() => setPendingAction("approve")}
                  >
                    {t("admin.payroll.periodsAndRuns.runDetail.approve")}
                  </MDButton>
                  <MDButton size="small" color="error" onClick={() => setRejectOpen(true)}>
                    {t("admin.payroll.periodsAndRuns.runDetail.reject")}
                  </MDButton>
                </>
              )}
              {run.status === "approved" && (
                <MDButton size="small" color="warning" onClick={() => setPendingAction("post")}>
                  {t("admin.payroll.periodsAndRuns.runDetail.post")}
                </MDButton>
              )}
              {run.status === "posted" && (
                <MDButton size="small" color="success" onClick={() => setPendingAction("pay")}>
                  {t("admin.payroll.periodsAndRuns.runDetail.pay")}
                </MDButton>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {loading ? (
        <LoadingScreen />
      ) : (
        <>
          {errorEmployees.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: "error.main" }}>
              <MDTypography variant="subtitle2" color="error" sx={{ mb: 1 }}>
                {t("admin.payroll.periodsAndRuns.runDetail.errorsTitle")}
              </MDTypography>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {errorEmployees.map((re) => (
                      <TableRow key={re.id}>
                        <TableCell>{employeeName(re.employeeId)}</TableCell>
                        <TableCell>{re.exclusionReason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          <Paper variant="outlined" sx={{ p: 2 }}>
            <MDTypography variant="subtitle2" sx={{ mb: 1 }}>
              {t("admin.payroll.periodsAndRuns.runDetail.resultsTitle")}
            </MDTypography>
            {results.length === 0 ? (
              <MDTypography variant="body2" color="text">
                {t("admin.payroll.periodsAndRuns.runDetail.noResults")}
              </MDTypography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {t("admin.payroll.employees.employee")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }} align="right">
                        {t("admin.payroll.periodsAndRuns.runDetail.fields.gross")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }} align="right">
                        {t("admin.payroll.periodsAndRuns.runDetail.fields.deductions")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }} align="right">
                        {t("admin.payroll.periodsAndRuns.runDetail.fields.netPay")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }} align="right">
                        {t("admin.payroll.periodsAndRuns.runDetail.fields.employerCost")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {t("admin.payroll.periodsAndRuns.runDetail.fields.currency")}
                      </TableCell>
                    </TableRow>
                    {results.map((result) => (
                      <TableRow key={result.id} hover>
                        <TableCell>{employeeName(result.employeeId)}</TableCell>
                        <TableCell align="right">{result.grossEarnings}</TableCell>
                        <TableCell align="right">{result.totalDeductions}</TableCell>
                        <TableCell align="right">
                          <strong>{result.netPay}</strong>
                        </TableCell>
                        <TableCell align="right">{result.employerCost}</TableCell>
                        <TableCell>{result.currency}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}

      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold" }}>
          {t("admin.payroll.periodsAndRuns.runDetail.reject")}
        </DialogTitle>
        <DialogContent>
          <TextField
            sx={{ mt: 1 }}
            label={t("admin.payroll.periodsAndRuns.runDetail.rejectionReason")}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton onClick={() => setRejectOpen(false)} color="secondary">
            {t("common.cancel")}
          </MDButton>
          <MDButton onClick={handleReject} color="error" disabled={!rejectReason.trim()}>
            {t("admin.payroll.periodsAndRuns.runDetail.reject")}
          </MDButton>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={!!pendingAction}
        onClose={() => setPendingAction(null)}
        onConfirm={() => confirmHandlers[pendingAction]?.()}
        message={pendingAction ? confirmMessages[pendingAction] : ""}
      />

      <SnackbarWrapper
        ref={snackbarRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </Box>
  );
};

RunDetailView.propTypes = {
  selectedPeriod: PropTypes.object,
  selectedRun: PropTypes.object,
  onRunUpdated: PropTypes.func,
  canEdit: PropTypes.bool.isRequired,
};

RunDetailView.defaultProps = {
  selectedPeriod: null,
  selectedRun: null,
  onRunUpdated: () => {},
};

export default RunDetailView;
